/**
 * Shared helpers for matching inventory location barcodes / typed text
 * (hardware wedge, camera scan, manual entry) across mobile devices.
 */

export function safeLocationPart(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function parseScannedValue(raw) {
  if (typeof raw === "string") return raw;
  if (raw && typeof raw.data === "string") return raw.data;
  if (raw?.nativeEvent && typeof raw.nativeEvent.data === "string") return raw.nativeEvent.data;
  if (raw == null) return "";
  return String(raw);
}

/** Strip control chars (Zebra/DataWedge often suffix \\r\\n) and normalize separators for comparison. */
export function normalizeScannedLocation(data) {
  var scanned = parseScannedValue(data)
    .replace(/[\x00-\x1F\x7F]/g, "")
    .trim();
  if (!scanned) return "";

  var scannedArray = scanned.split("-");
  if (scannedArray.length <= 1) {
    scannedArray = scanned.split("/");
  }
  if (scannedArray.length > 1) {
    scanned = "";
    scannedArray.forEach((part) => {
      scanned += part.trim().replace(/ /g, "");
    });
  } else {
    scanned = scanned.replace(/ /g, "");
  }
  return scanned.toLowerCase();
}

export function renderLocationLabel(location) {
  var item = location;
  if (typeof item === "object" && item?.length > 0) {
    item = item[0];
  } else if (typeof item === "string") {
    return item;
  }
  if (!item || typeof item !== "object") return "";

  var completeLocation = "";
  if (item?.site?.locationName) completeLocation += safeLocationPart(item.site.locationName);
  if (item?.zone?.locationName) completeLocation += "-" + safeLocationPart(item.zone.locationName);
  if (item?.aisle?.locationName) completeLocation += "-" + safeLocationPart(item.aisle.locationName);
  if (item?.rackBay?.locationName) completeLocation += "-" + safeLocationPart(item.rackBay.locationName);
  if (item?.shelfLevel?.locationName) completeLocation += "-" + safeLocationPart(item.shelfLevel.locationName);
  if (item?.bin?.locationName) completeLocation += "-" + safeLocationPart(item.bin.locationName);
  return completeLocation;
}

/** Concatenated path without separators (legacy scan format). */
export function getLocationMatchKey(location) {
  var completeLocation = "";
  if (location?.site?.locationName) completeLocation += safeLocationPart(location.site.locationName);
  if (location?.zone?.locationName) completeLocation += safeLocationPart(location.zone.locationName);
  if (location?.aisle?.locationName) completeLocation += safeLocationPart(location.aisle.locationName);
  if (location?.rackBay?.locationName) completeLocation += safeLocationPart(location.rackBay.locationName);
  if (location?.shelfLevel?.locationName) completeLocation += safeLocationPart(location.shelfLevel.locationName);
  if (location?.bin?.locationName) completeLocation += safeLocationPart(location.bin.locationName);
  return completeLocation.trim().replace(/ /g, "").replace(/[/-]+/g, "").toLowerCase();
}

function uniqueByLocationId(candidates) {
  var seen = {};
  return candidates.filter((loc) => {
    var key = getLocationMatchKey(loc);
    if (!key || seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

/**
 * Resolve a scan or typed string to one inventory location object, or null if ambiguous / not found.
 */
export function findLocationInList(scannedRaw, locations) {
  if (!Array.isArray(locations) || !locations.length) return null;

  var norm = normalizeScannedLocation(scannedRaw);
  if (!norm) return null;

  var exactKey = locations.filter((loc) => getLocationMatchKey(loc) === norm);
  exactKey = uniqueByLocationId(exactKey);
  if (exactKey.length === 1) return exactKey[0];

  var exactLabel = locations.filter(
    (loc) => normalizeScannedLocation(renderLocationLabel(loc)) === norm
  );
  exactLabel = uniqueByLocationId(exactLabel);
  if (exactLabel.length === 1) return exactLabel[0];

  var suffixMatches = locations.filter((loc) => {
    var key = getLocationMatchKey(loc);
    return key && (key.endsWith(norm) || norm.endsWith(key));
  });
  suffixMatches = uniqueByLocationId(suffixMatches);
  if (suffixMatches.length === 1) return suffixMatches[0];

  var includesMatches = locations.filter((loc) => {
    var key = getLocationMatchKey(loc);
    var labelNorm = normalizeScannedLocation(renderLocationLabel(loc));
    return (
      (key && (key.includes(norm) || norm.includes(key))) ||
      (labelNorm && (labelNorm.includes(norm) || norm.includes(labelNorm)))
    );
  });
  includesMatches = uniqueByLocationId(includesMatches);
  if (includesMatches.length === 1) return includesMatches[0];

  return null;
}

export function locationListIncludesTerm(locations, term) {
  var norm = normalizeScannedLocation(term);
  if (!norm) return false;
  return locations.some((loc) => {
    var key = getLocationMatchKey(loc);
    var labelNorm = normalizeScannedLocation(renderLocationLabel(loc));
    return (
      key === norm ||
      labelNorm === norm ||
      key.includes(norm) ||
      labelNorm.includes(norm)
    );
  });
}
